import type { LocationQuery, LocationQueryValue } from 'vue-router'
import type { UserLogin } from './interface'
import { useModalWelcome } from '~/composables/event/use-modal-welcome'
import { useCurrentUserStore } from '~/stores/user-store.store.ts'
import { AUTH_API } from '~/configs/api'
import { useModal } from '~/composables/common/use-modal'
import { useApi } from '~/composables/common/use-api'
import { useError } from '~/composables/common/use-error'
import { usePromotion } from '~/composables/promotion/use-promotion'
import { useDepositData } from '~composables/deposit/use-deposit-data'
import { ModalCollection, SECONDS_PER_DAY, COOKIE_MAX_AGE_DAYS } from '~/constants'
import { ResponseStatus } from '~/composables/common/interface'
import { EVENT } from '~/configs/page-url'
import { useEventEntryHalloween } from '~/composables/events/halloween/use-event-entry'

export function useLogin() {
  const gtm = useGtm()
  const { request } = useApi()
  const store = useCurrentUserStore()
  const user = reactive<UserLogin>({ username: '', password: '' })
  const messageError = ref('')
  const { closeAllModal, openModal } = useModal()
  const { showError } = useError()
  const { setPromotion, getPromotionPackages } = usePromotion()
  const router = useRouter()
  const route = useRoute()
  const { initListAgency } = useDepositData()
  const { openModalEvent } = useModalWelcome()
  const { openModalEventHalloWeen } = useEventEntryHalloween()
  const {
    IS_500K_EVENT_ENABLED,
    EVENT_PREMIER_LEAGUE_RANKING_ENABLED,
    EVENT_HALLOWEEN_ENABLE,
    EVENT_PREMIER_LEAGUE_RANKING_POPUP_ENABLED,
    EVENT_HALLOWEEN_POPUP_ENABLE
  } = useRuntimeConfig().public

  const isLogged = computed((): boolean => {
    if (import.meta.client) {
      return !!store.data?.token
    }
    return !!useCookie('token').value
  })

  function resetError(): void {
    messageError.value = ''
  }

  async function handleSubmit(lpToken?: string | LocationQueryValue[], redirect?: LocationQuery): Promise<void> {
    messageError.value = ''
    const { username, password } = user
    try {
      let response = null
      if (lpToken) {
        response = await request(AUTH_API.LP_TOKEN_LOG_IN, {
          method: 'POST',
          body: {
            token: lpToken
          }
        })
      } else {
        response = await request(AUTH_API.LOG_IN, {
          method: 'POST',
          body: {
            username,
            password
          }
        })
      }
      const status = response?.status
      const data = response?.data
      const message = response?.message
      if (status === ResponseStatus.OK && data) {
        // login success
        if (!lpToken) {
          gtm?.trackEvent({ event: 'formSubmitted', formName: 'Form_Login' })
        }
        closeAllModal()
        store.setCurrentUser(data)
        if (data.needFullnameUpdated) {
          openModal(ModalCollection.UPDATE_DISPLAYNAME)
        }
        useCookie('token', { maxAge: SECONDS_PER_DAY * COOKIE_MAX_AGE_DAYS }).value = data.token
        await setPromotion()
        await getPromotionPackages()
        await initListAgency()
        sessionStorage.setItem('isLogged', 'true')

        const redirectUrl = localStorage.getItem('redirect_after_login')
        if (redirectUrl) {
          window.location.href = `${redirectUrl}?token=${store.data.token}`
          localStorage.removeItem('redirect_after_login')
          return
        }

        if (
          IS_500K_EVENT_ENABLED
          && !EVENT_PREMIER_LEAGUE_RANKING_ENABLED
          && !EVENT_HALLOWEEN_ENABLE
        ) {
          openModal(ModalCollection.FIVEH_K_PROMOTION)
        }

        if (
          EVENT_PREMIER_LEAGUE_RANKING_ENABLED
          && EVENT_PREMIER_LEAGUE_RANKING_POPUP_ENABLED
          && route.path !== EVENT.PREMIER_LEAGUE_RANKING
        ) {
          closeAllModal()
          openModalEvent()
        }

        if (
          EVENT_HALLOWEEN_ENABLE
          && EVENT_HALLOWEEN_POPUP_ENABLE
          && route.path !== EVENT.HALLOWEEN
        ) {
          closeAllModal()
          openModalEventHalloWeen()
        }

        if (redirect && route.path !== EVENT.PREMIER_LEAGUE_RANKING && route.path !== EVENT.HALLOWEEN) {
          void router.push(redirect)
          return
        }
      } else if (status === ResponseStatus.LOGIN_FAILED_MANY_TIMES_ERROR) {
        messageError.value = 'Quý khách đã đăng nhập sai quá nhiều lần. Vui lòng liên hệ CSKH'
      } else {
        // login failed
        messageError.value = 'Đã xảy ra lỗi. Vui lòng kiểm tra lại!'
        if (message) {
          messageError.value = message
        }
      }
    } catch {
      messageError.value = 'Đã xảy ra lỗi. Vui lòng kiểm tra lại!'
    }
    // Show error
    if (messageError) {
      showError(messageError.value)
    }
  }

  function showModalLogin(forceCloseAllModal: boolean = true): void {
    openModal(ModalCollection.LOGIN_AND_SIGN_UP, ModalCollection.LOGIN, forceCloseAllModal)
  }

  const openAuthModalByQuery = () => {
    const authModalType = route.query.modal
    if (authModalType === ModalCollection.LOGIN || authModalType === ModalCollection.SIGN_UP) {
      openModal(ModalCollection.LOGIN_AND_SIGN_UP, authModalType, true)
    }
  }

  function isButtonDisabled(
    isSubmitting: boolean,
    errors: Partial<Record<string, string | undefined>>,
    messageError: string
  ): boolean {
    const hasErrors = Object.keys(errors).length > 0 || messageError.length > 0
    return isSubmitting || !user.username || !user.password || hasErrors
  }
  return {
    user,
    messageError,
    isLogged,
    handleSubmit,
    showModalLogin,
    isButtonDisabled,
    openAuthModalByQuery,
    resetError
  }
}
